import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@material-ui/core'
import { isEmpty, noop } from 'lodash'
import { TEXT_VARIANTS, DATE_PRESET, BUTTON_SIZES, DATE_TYPES, CHART_CURVE_INTERPOLATIONS } from '../../../constants'
import Tag from '../../atoms/Tag'
import DateRangePicker from '../../molecules/DateRangePicker'
import LineChartNivo from '../../molecules/LineChartNivo'
import Skeleton from '../../atoms/Skeleton'
import WebOnlyContent from '../../atoms/WebOnlyContent'
import EmptySection from '../../atoms/EmptySection'
import Text from '../../atoms/Text'
import Select from '../../molecules/Select'
import { defaultOptions } from '../../molecules/RelativeDateSelect'
import { localStorageHelper } from '../../../utils/localStorageHelper'
import { capitalizeFirstLetter } from '../../../utils'
import { useWidgetContext } from '../../molecules/WidgetWrapper'
import { useLineChart } from './useLineChart'

const dateIntervalOptions = Object.values(DATE_TYPES).map((dateType) => ({
  key: dateType,
  value: dateType,
  label: capitalizeFirstLetter(dateType)
}))

const LineChartContainerV3 = ({
  lineChartProps,
  showTagLabel,
  series,
  assetFilters,
  defaultGraphDate,
  onGraphDateChange,
  identifiersMapper,
  dateRangeOptions,
  showPlottingOptions,
  defaultIdentifier,
  showSeriesTitle,
  dateIntervalOptions,
  defaultDataInterval,
  // if true, the chart will only show date period in which datasets intersect
  showDataSetsIntersection,
  defaultFilter: _defaultFilter,
  configurationStorageKey: _configurationStorageKey,
  intervalBreakpoints
}) => {
  const configurationStorageKey = _configurationStorageKey
    ? `line_chart_${_configurationStorageKey}`
    : undefined

  const defaultValues = useMemo(() => {
    const identifier = defaultIdentifier || series?.[0]?.identifier || 'endingValue'
    const defaultConfiguration = {
      dateRange: null,
      dataInterval: defaultDataInterval || DATE_TYPES.day,
      graphCurve: lineChartProps?.curve ?? CHART_CURVE_INTERPOLATIONS.monotoneX,
      graphDate: defaultGraphDate,
      identifier
    }
    if (!configurationStorageKey) return defaultConfiguration
    const configuration = localStorageHelper.load(configurationStorageKey)
    return configuration || defaultConfiguration
  }, [
    series,
    defaultIdentifier,
    defaultGraphDate,
    lineChartProps?.curve,
    defaultDataInterval,
    configurationStorageKey
  ])

  const { subscribedFilters } = useWidgetContext()

  const defaultFilter = useMemo(() => {
    return Object.assign({}, _defaultFilter, subscribedFilters?.levelFilters)
  }, [_defaultFilter, subscribedFilters?.levelFilters])

  const lineChart = useLineChart({
    identifiersMapper,
    assetFilters,
    series,
    lineChartProps,
    onGraphDateChange,
    defaultFilter,
    dateRangeOptions,
    defaultValues,
    configurationStorageKey,
    dateIntervalOptions,
    intervalBreakpoints
  })

  const {
    historicalIdentifierOptions,
    identifiersLoading,
    loading,
    showDateRangePicker,
    onSelectIdentifier,
    selectedIdentifier,
    baseLineChartProps,
    availableDates,
    onAvailableDateSelect,
    onDateRangeChange,
    selectedGraphDate,
    graphDateRangeOptions,
    dateInterval,
    chartCurve
  } = lineChart

  const renderIdentifiersSelector = useMemo(() => {
    if (defaultIdentifier) {
      const seriesData = series.find(
        ({ identifier }) => defaultIdentifier === identifier
      )
      if (!showSeriesTitle) return null
      return (
        <Text
          variant={TEXT_VARIANTS.body1}
          text={seriesData?.title || seriesData?.label}
        />
      )
    }
    if (identifiersLoading) {
      return <Skeleton height='2rem' width='10rem' />
    }

    const options = historicalIdentifierOptions.filter(({ value }) => {
      return series.findIndex(({ identifier }) => identifier === value) !== -1
    })

    return (
      <Select
        size={BUTTON_SIZES.small}
        value={selectedIdentifier}
        options={options}
        onChange={onSelectIdentifier}
        showCheckMarOnSelectedItems
      />
    )
  }, [
    series,
    showSeriesTitle,
    defaultIdentifier,
    identifiersLoading,
    selectedIdentifier,
    onSelectIdentifier,
    historicalIdentifierOptions
  ])

  if (!loading && isEmpty(baseLineChartProps)) {
    return <EmptySection title='No Data Available' emptyDescription />
  }

  return (
    <>
      <Box display='flex' width='100%' alignItems='center'>
        {renderIdentifiersSelector}
        {showTagLabel && (
          <Box ml='1rem'>
            <Tag label='Filtered' />
          </Box>
        )}
        <Box display='flex' flexDirection='row' ml='auto'>
          {showDateRangePicker && (
            <Box mr='2rem'>
              <DateRangePicker
                datePickerWidth='12rem'
                defaultStartDate={null}
                defaultEndDate={null}
                onChange={onDateRangeChange}
                minDate={availableDates.min}
                maxDate={availableDates.max}
              />
            </Box>
          )}
          <WebOnlyContent>
            {showPlottingOptions.dateRange ? (
              <Select
                size={BUTTON_SIZES.small}
                value={selectedGraphDate}
                onChange={onAvailableDateSelect}
                options={graphDateRangeOptions}
                readOnly={graphDateRangeOptions.length <= 1}
              />
            ) : null}
            {showPlottingOptions.dateInterval ? (
              <Select {...dateInterval} />
            ) : null}
            {showPlottingOptions.chartCurve ? (
              <Select {...chartCurve} />
            ) : null}
          </WebOnlyContent>
        </Box>
      </Box>
      {loading && (
        <Box mt='2rem' width='100%'>
          <Skeleton height='20rem' width='100%' />
        </Box>
      )}
      {!loading && (
        <LineChartNivo
          {...baseLineChartProps}
          dateInterval={dateInterval.value}
          showDataSetsIntersection={showDataSetsIntersection}
        />
      )}
    </>
  )
}

LineChartContainerV3.propTypes = {
  lineChartProps: PropTypes.object,
  showTagLabel: PropTypes.bool,
  series: PropTypes.array,
  assetFilters: PropTypes.object,
  identifiersMapper: PropTypes.any,
  defaultGraphDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
    .isRequired,
  onGraphDateChange: PropTypes.func,
  dateRangeOptions: PropTypes.array,
  showPlottingOptions: PropTypes.shape({
    dateInterval: PropTypes.bool,
    chartCurve: PropTypes.bool,
    dateRange: PropTypes.bool
  }),
  levelFilters: PropTypes.shape({
    benchmarkType: PropTypes.string
  }),
  defaultIdentifier: PropTypes.string,
  configurationStorageKey: PropTypes.string,
  dateIntervalOptions: PropTypes.shape({
    key: PropTypes.oneOf(Object.values(DATE_TYPES)),
    value: PropTypes.oneOf(Object.values(DATE_TYPES)),
    label: PropTypes.string
  }),
  defaultDataInterval: PropTypes.string,
  showSeriesTitle: PropTypes.bool,
  defaultFilter: PropTypes.object,
  showDataSetsIntersection: PropTypes.bool,
  /* Determines the correlation between number of days and a date interval for automatic date intervals */
  intervalBreakpoints: PropTypes.shape({
    day: PropTypes.number,
    month: PropTypes.number,
    quarter: PropTypes.number,
    year: PropTypes.number
  })
}

LineChartContainerV3.defaultProps = {
  lineChartProps: {},
  showTagLabel: false,
  series: [
    {
      identifier: 'endingValue',
      dataPoints: [
        {
          label: 'Market Value',
          key: 'endingValue',
          color: '#3C5357',
          lineChartConfig: { enableArea: true }
        },
        {
          label: 'Net Additions',
          key: 'adjustedNetAdditions',
          color: '#bca167',
          lineChartConfig: { enableArea: false }
        }
      ]
    },
    {
      identifier: 'cumulativeReturn',
      dataPoints: [
        {
          label: 'Return',
          key: 'cumulativeReturn',
          color: 'green'
        }
      ]
    },
    {
      identifier: 'cumulativeBenchmark',
      dataPoints: [
        {
          label: 'Net Additions',
          key: 'adjustedNetAdditions',
          color: '#bca167'
        },
        {
          benchmarks: true,
          key: 'benchmarks.benchmarkValue',
          color: ['#8ac7fe', '#8afefe']
        }
      ]
    }
  ],
  assetFilters: {},
  identifiersMapper: 'skipCumulativeBenchmarkMapper',
  onGraphDateChange: noop,
  defaultGraphDate: DATE_PRESET.L12,
  dateRangeOptions: defaultOptions,
  showPlottingOptions: {
    dateInterval: true,
    chartCurve: false,
    dateRange: true
  },
  levelFilters: {},
  defaultIdentifier: undefined,
  configurationStorageKey: undefined,
  defaultDataInterval: undefined,
  dateIntervalOptions,
  showSeriesTitle: true,
  showDataSetsIntersection: undefined
}

export default LineChartContainerV3
